import { useEffect, useRef } from 'react';
import { useLocationStore } from '../store/useLocationStore';
import { useAddressCapture } from '../hooks/useAddressCapture';

/** Re-read a device fix at most once per this window on app-open / focus. */
const DEVICE_FIX_TTL_MS = 30 * 60 * 1000;

/**
 * Headless guard that keeps `locationSource: 'device'` honest. Distance is only
 * ever exposed for device-sourced locations, but a device fix (coords + the
 * 'device' stamp) is persisted to localStorage and never re-polled on its own —
 * so this watches the geolocation permission and:
 *
 *  - Downgrades off 'device' when permission is no longer 'granted' (revoked),
 *    which hides distance immediately. Cached coords are left in place so the
 *    feed keeps working — only the distance affordance goes away.
 *
 *  - Refreshes a stale device fix on app-open and tab/PWA focus
 *    (`visibilitychange` → visible), throttled to once per DEVICE_FIX_TTL_MS so
 *    a moving user's coords/distance stay current without polling on every
 *    quick app switch (the persisted `deviceFixAt` makes the throttle survive
 *    reloads). Gated to silent, already-granted permission and skipped when the
 *    user deliberately switched their operating locale to a typed ZIP
 *    (`locationSource === 'postal'`) — that choice stands until they turn device
 *    location back on themselves.
 *
 * Note what this guard deliberately does NOT do: it never PROMOTES a non-device
 * source to 'device'. Auto-capturing on a permission grant is scoped to the
 * surfaces that explicitly ask the user to enable location (the locale
 * switcher, the distance gate, the first-run prompt) via
 * `useAutoCaptureOnPermissionGrant` — so granting permission while just browsing
 * the feed does not silently move the user's browse location. The guard's
 * focus-refresh only ever refreshes an ALREADY-device fix; it can't switch a
 * ZIP/null source on.
 *
 * Degrades gracefully: if the Permissions API (or the 'geolocation' descriptor)
 * is unavailable — e.g. some native webviews / older Safari — we leave the
 * persisted snapshot untouched rather than guessing.
 *
 * Mount once near the app root, after the store is available.
 */
export const DeviceLocationGuard: React.FC = () => {
  const setLocationSource = useLocationStore((s) => s.setLocationSource);
  const { handleUseDeviceLocation } = useAddressCapture();

  // Stable ref so the focus-refresh always calls the latest capture fn without
  // re-subscribing the effect on every render.
  const captureRef = useRef(handleUseDeviceLocation);
  captureRef.current = handleUseDeviceLocation;

  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.permissions?.query) return;

    let cancelled = false;
    let status: PermissionStatus | null = null;

    // Downgrade-only: when permission is anything other than 'granted', a
    // device fix can no longer promise an accurate distance, so drop the
    // provenance (cached coords stay so the feed keeps working). Promotion the
    // other way is owned by the capture surfaces, not this guard.
    const evaluate = (state: PermissionState) => {
      if (cancelled) return;
      // Read the latest source from the store (not a stale closure) so a
      // capture that lands between renders isn't clobbered.
      const source = useLocationStore.getState().locationSource;
      if (state !== 'granted' && source === 'device') {
        setLocationSource(null);
      }
    };

    // App-open / focus refresh. Silent: only when permission is already
    // 'granted' (no prompt), only for an existing device fix, never when the
    // user has deliberately switched to a typed ZIP, and at most once per TTL.
    const maybeRefresh = () => {
      if (cancelled || status?.state !== 'granted') return;
      const { locationSource, deviceFixAt } = useLocationStore.getState();
      // Not on a device fix (incl. a typed ZIP, which sets 'postal') → nothing
      // to refresh; the user's chosen locale stands until they re-enable device.
      if (locationSource !== 'device') return;
      if (Date.now() - (deviceFixAt ?? 0) < DEVICE_FIX_TTL_MS) return;
      captureRef.current();
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') maybeRefresh();
    };

    navigator.permissions
      .query({ name: 'geolocation' as PermissionName })
      .then((result) => {
        if (cancelled) return;
        status = result;
        evaluate(result.state);
        result.onchange = () => evaluate(result.state);
        // App-open refresh — runs once the permission state is known.
        maybeRefresh();
      })
      .catch(() => {
        /* Permissions API unsupported — keep the persisted snapshot as-is. */
      });

    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      if (status) status.onchange = null;
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [setLocationSource]);

  return null;
};
